import { useMemo, useState } from 'react';

import DocumentsStepIcon from 'src/assets/step-icon-documents.svg?react';
import PicturesStepIcon from 'src/assets/step-icon-pictures.svg?react';
import { useProductTypeVendorLeadSpecification } from 'src/hooks/useProductTypeVendorLeadSpecification';
import {
  type IVendorStepInfo,
  type IVendorsContext,
  VendorPageStep,
} from 'src/pages/VendorsPage/VendorsPage.context.types';
import { ProductSpecificationFieldGroup } from 'src/types/reference/ProductSpecificationFieldGroup';
import type { IVendorLeadProduct } from 'src/types/vendors/IVendorLeadProduct';

export function useVendorsContextCreator() {
  const [isEditing, setIsEditing] = useState(false);

  const [isMobileGuideOpened, setIsMobileGuideOpened] = useState(false);

  const [isLastVendorStep, setIsLastVendorStep] = useState(false);

  const [vendorStep, setVendorStep] = useState<VendorPageStep>(VendorPageStep.Start);
  const [vendorStepGuideKey, setVendorStepGuideKey] = useState<string | null>(null);

  const [vendorStepsInfo] = useState<IVendorStepInfo[]>([
    {
      step: VendorPageStep.Pictures,
      description: 'Pictures',
      icon: <PicturesStepIcon />,
    },
    {
      step: VendorPageStep.Description,
      description: 'Description',
      icon: <DocumentsStepIcon />,
    },
    {
      step: VendorPageStep.Condition,
      description: 'Last Contained & Previous Use',
      icon: <DocumentsStepIcon />,
    },
    {
      step: VendorPageStep.Contact,
      description: 'Address & Contact',
      icon: <DocumentsStepIcon />,
    },
  ]);

  const [vendorLeadProduct, setVendorLeadProduct] = useState<IVendorLeadProduct | null>(null);

  const vendorLeadProductSpecification = useProductTypeVendorLeadSpecification(vendorLeadProduct?.productTypeId);

  const vendorsContext = useMemo<IVendorsContext>(
    () => ({
      isEditing,

      isMobileGuideOpened,

      isLastVendorStep,

      vendorStep,
      vendorStepsInfo,
      vendorStepGuideKey,

      vendorLeadProduct,
      vendorLeadProductSpecification,

      onEditing: (newIsEditing: boolean) => setIsEditing(newIsEditing),

      toggleMobileGuideOpened: () => {
        setIsMobileGuideOpened((prev) => !prev);
      },

      goNextStep: () => {
        const newStep =
          vendorStep === VendorPageStep.Start
            ? VendorPageStep.Pictures
            : vendorStep === VendorPageStep.Pictures
              ? VendorPageStep.Description
              : vendorStep === VendorPageStep.Description
                ? VendorPageStep.Condition
                : vendorStep === VendorPageStep.Condition
                  ? VendorPageStep.Contact
                  : VendorPageStep.Done;

        setVendorStep(newStep);

        setIsLastVendorStep(false);

        const newStepGuideKey =
          newStep === VendorPageStep.Pictures
            ? ProductSpecificationFieldGroup.Pictures
            : newStep === VendorPageStep.Description
              ? ProductSpecificationFieldGroup.Quantities
              : newStep === VendorPageStep.Contact
                ? ProductSpecificationFieldGroup.Contacts
                : null;

        setVendorStepGuideKey(newStepGuideKey);

        setIsEditing(false);
      },

      goBack: () => {
        const newStep =
          vendorStep === VendorPageStep.Done
            ? VendorPageStep.Contact
            : vendorStep === VendorPageStep.Contact
              ? VendorPageStep.Condition
              : vendorStep === VendorPageStep.Condition
                ? VendorPageStep.Description
                : vendorStep === VendorPageStep.Description
                  ? VendorPageStep.Pictures
                  : VendorPageStep.Start;

        setVendorStep(newStep);

        setIsLastVendorStep(false);

        const newStepGuideKey =
          newStep === VendorPageStep.Pictures
            ? ProductSpecificationFieldGroup.Pictures
            : newStep === VendorPageStep.Description
              ? ProductSpecificationFieldGroup.Quantities
              : newStep === VendorPageStep.Contact
                ? ProductSpecificationFieldGroup.Contacts
                : null;

        setVendorStepGuideKey(newStepGuideKey);

        setIsEditing(false);
      },

      setLastVendorStep: (newIsLastVendorStep: boolean) => setIsLastVendorStep(newIsLastVendorStep),

      setVendorStep: (newVendorStep: VendorPageStep) => {
        setVendorStep(newVendorStep);

        setIsLastVendorStep(false);

        const newStepGuideKey =
          newVendorStep === VendorPageStep.Pictures
            ? ProductSpecificationFieldGroup.Pictures
            : newVendorStep === VendorPageStep.Description
              ? ProductSpecificationFieldGroup.Quantities
              : newVendorStep === VendorPageStep.Contact
                ? ProductSpecificationFieldGroup.Contacts
                : null;

        setVendorStepGuideKey(newStepGuideKey);

        setIsEditing(false);
      },
      setVendorStepGuideKey: (newVendorStepGuide: string | null) => setVendorStepGuideKey(newVendorStepGuide),

      updateVendorLeadProduct: (updatedVendorLeadProduct: IVendorLeadProduct) =>
        setVendorLeadProduct((prev) => ({
          ...(prev ?? {}),

          ...updatedVendorLeadProduct,
        })),
    }),
    [
      isEditing,
      isMobileGuideOpened,
      isLastVendorStep,
      vendorStep,
      vendorStepsInfo,
      vendorStepGuideKey,
      vendorLeadProduct,
      vendorLeadProductSpecification,
    ]
  );

  return vendorsContext;
}
